import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@punto/ui/components/alert";
import { Button } from "@punto/ui/components/button";

import { config } from "@/lib/config";
import { applyExtensionUpdate } from "@/lib/extension/apply-extension-update";
import { useExtensionConnection } from "@/lib/extension/extension-connection";

const FIVE_MINUTES = 5 * 60 * 1000;

interface VersionResponse {
  version: string;
}

const getVersion = () =>
  fetch(`/version.json?v=${Date.now()}`).then((res) => res.json());

const UpdateChecker = () => {
  const { t } = useTranslation();

  const { data: version } = useQuery<VersionResponse>({
    queryKey: ["version"],
    queryFn: getVersion,
    refetchInterval: FIVE_MINUTES,
  });

  // P-2789: the extension also restarted under this tab (its content script is
  // gone) once the connection reports a new version.
  const { updated: extensionUpdated } = useExtensionConnection();
  const [isUpdating, setIsUpdating] = useState(false);

  const onUpdate = useCallback(() => {
    setIsUpdating(true);
    void applyExtensionUpdate().finally(() => {
      window.location.reload();
    });
  }, []);

  const janusUpdated = Boolean(
    version?.version && version.version !== config.BUILD_VERSION,
  );

  if (!janusUpdated && !extensionUpdated) {
    return null;
  }

  return (
    <Alert variant="info" aria-live="polite">
      <div className="container flex items-center">
        <div className="flex-1">
          <AlertTitle>{t("UpdateChecker.title")}</AlertTitle>
          <AlertDescription>
            {extensionUpdated
              ? t("UpdateChecker.extension_description")
              : t("UpdateChecker.description")}
          </AlertDescription>
        </div>
        <div>
          <Button onClick={onUpdate} disabled={isUpdating}>
            {t("UpdateChecker.update")}
          </Button>
        </div>
      </div>
    </Alert>
  );
};

export { UpdateChecker };
